import { snakeCase } from 'snake-case';

import apiFn from '../api-service';
import { FullPerson, Person, PossibleIncludes } from './types';

interface Headers {
  Authorization?: string;
  Accept?: string;
  'x-nested-list-partial-updates'?: string;
}

const api = (
  id: string,
  headers: Headers = {
    Accept: 'application/vnd.eventmobi+json; version=p.8',
    'x-nested-list-partial-updates': 'false',
  },
) => apiFn(`/api/uapi/events/${id}/people`, headers, !headers.Authorization);

export const list = <TIncludes extends PossibleIncludes = undefined>(
  eventId: string,
  payload: {
    offset?: number;
    limit?: number;
    filterDeletionRequested?: boolean;
    sortByDeletionRequestedAt?: boolean;
  } = {},
  query = null,
  include = ['groups', 'profileImage'],
  token?: string,
): APIResponse<Person<TIncludes>[]> => {
  const params = new URLSearchParams(query);

  const headers: Headers = {
    Accept: 'application/vnd.eventmobi+json; version=p.9',
  };
  let sort = (params.get('sort') ?? '').split(',').filter(Boolean);

  if (include.length > 0) {
    params.set('include', include.map(v => snakeCase(v)).join(','));
  }

  if (typeof payload.limit !== 'undefined') {
    params.set('limit', String(payload.limit));
  }

  if (payload.offset > 0) {
    params.set('page', String(payload.offset / payload.limit));
  }

  if (payload.filterDeletionRequested) {
    params.set('deletionRequested', 'true');
  }

  if (payload.sortByDeletionRequestedAt) {
    sort = ['deletionRequestedAt', ...sort];
  }

  if (sort.length > 0) {
    params.set(
      'sort',
      sort.map(v => (v.startsWith('-') ? `-${snakeCase(v.substring(1))}` : snakeCase(v))).join(','),
    );
  }

  if (token) {
    headers.Authorization = `Basic ${token}`;
  }

  return api(eventId, headers)
    .get(null, { params })
    .then(response => response.data);
};

export async function listAll(eventId: string) {
  // The API handler doesn't pass 'limit' directly, so we need a full request
  // to get the total count
  const firstResult = await list(eventId);
  const count = firstResult.meta.pagination.totalItemsCount;

  const requests = [];

  // Fetch the remaining pages in parallel
  let offset = 1000;
  while (offset < count) {
    requests.push(list(eventId, { offset, limit: 1000 }));

    offset += 1000;
  }

  const results = await Promise.all(requests);
  results.splice(0, 0, firstResult);

  return results.map(result => result.data).flat();
}

export function remove(eventId: string, id: string) {
  return api(eventId)
    .delete(id)
    .then(response => response?.data);
}

export async function update(eventId: string, id: string, payload = {}) {
  const updatedPerson = await api(eventId)
    .patch(id, payload)
    .then(response => response?.data?.data);

  return { updatedPerson };
}

export function create(eventId: string, payload = {}) {
  return api(eventId)
    .post('', payload)
    .then(response => response?.data);
}

export function get(eventId: string, id: string, include = []) {
  const params = new URLSearchParams();

  include.push(
    'ticket',
    'groups',
    'custom_fields',
    'profile_image',
    'session_roles',
    'documents',
    'external_links',
    'self_edit_metadata',
  );

  params.set('include', include.join(','));

  return api(eventId)
    .get(id, { params })
    .then(response => response?.data);
}

// Staff-only, self-only: creates the acting staff member's support profile in
// the event. Fails with a `person_exists` application error when the staff
// member already has a person record in the event.
export function createStaffProfile(eventId: string) {
  return apiFn(`/api/uapi/staff/events/${eventId}/profile`, {
    Accept: 'application/vnd.eventmobi+json; version=p.10',
  })
    .post('', {})
    .then(response => response?.data);
}

export function bulkUpdate(eventId: string, peopleIds: string[], payload: FullPerson) {
  return api(eventId, { Accept: 'application/vnd.eventmobi+json; version=p.9' })
    .patch('bulk-update', {
      query: {
        id: peopleIds,
      },
      updateData: payload,
    })
    .then(response => response?.data?.data);
}
